import { faArrowDown, faArrowUp, faMinus } from '@fortawesome/pro-solid-svg-icons';
import React from 'react';
import { globalStateCTX } from '../../GlobalState/GlobalState';
import { useNonce } from '../../Utils/hooks';
import { formatCurrency, getComparedPercentage } from '../../Utils/number';
import NumberFormater from '../NumberFormater/NumberFormater';
import KpiCard, { KPIStatus } from './KPICard/KpiCard';
import colors from '../../Utils/Colors/Colors.module.scss';
import { YTQStatisticsReadView } from '../../GeneratedServices';
import { statisticsApi } from '../../Http/Http';
import { useTranslation } from 'react-i18next';
import { FilterValuesType } from '../../FiltersProvider/FiltersProvider';

interface Props {
  filterValues?: FilterValuesType;
  propertyId?: number;
}
const YearlyRollingCostKpi: React.FC<Props> = (props) => {
  const { t } = useTranslation('common');

  const { handleHttpErrors } = React.useContext(globalStateCTX);

  const [yearlyCostStatistics, setYearlyCostStatistics] = React.useState<YTQStatisticsReadView>();
  const [status, setStatus] = React.useState<KPIStatus>(KPIStatus.Loading);
  const [shouldReload, reload] = useNonce();

  React.useEffect(() => {
    const abortController = new AbortController();

    const load = async () => {
      try {
        setStatus(KPIStatus.Loading);
        const { data } = await statisticsApi.statisticsResourceReadCostYTQStatistics(
          {
            ...props.filterValues,
            propertyId: props.propertyId
          },
          {
            signal: abortController.signal
          }
        );

        setYearlyCostStatistics({
          value: data.value * -1,
          businessPlanValue: data.businessPlanValue * -1,
          valuePerArea: data.valuePerArea * -1
        });
        setStatus(KPIStatus.None);
      } catch (error) {
        handleHttpErrors(error) && setStatus(KPIStatus.LoadError);
      }
    };
    load();

    return () => abortController.abort();
  }, [props.filterValues, props.propertyId, handleHttpErrors, shouldReload]);

  const formattedValue = yearlyCostStatistics
    ? formatCurrency(yearlyCostStatistics?.value, { forceCurrency: 'KSEK' })
    : undefined;
  const comparedCost = yearlyCostStatistics
    ? getComparedPercentage(yearlyCostStatistics.value, yearlyCostStatistics.businessPlanValue)
    : 0;
  const formattedValuePerArea = yearlyCostStatistics
    ? formatCurrency(yearlyCostStatistics.valuePerArea, { forceCurrency: 'SEK', unit: t('sekSqm') })
    : undefined;

  const hasBussinesPlanValue = yearlyCostStatistics?.businessPlanValue;
  return (
    <KpiCard
      title={t('yearlyRollingCosts')}
      value={
        <NumberFormater value={formattedValue?.value} additionalProps={{ decimalScale: 0, allowNegative: true }} />
      }
      valueSuffix={formattedValue?.suffix}
      additionalValue={
        <NumberFormater
          value={formattedValuePerArea?.value}
          additionalProps={{ decimalScale: 0, allowNegative: true }}
        />
      }
      additionalValueSuffix={formattedValuePerArea?.suffix}
      extra={
        hasBussinesPlanValue ? (
          <NumberFormater value={comparedCost} suffix={t('vsBusinessPlan')} />
        ) : (
          t('noBusinessPlanInformation')
        )
      }
      extraColor={hasBussinesPlanValue ? (comparedCost && comparedCost < 0 ? colors.green : colors.red) : colors.grey}
      extraIcon={hasBussinesPlanValue ? (comparedCost && comparedCost > 0 ? faArrowUp : faArrowDown) : faMinus}
      includeExtra
      status={status}
      retry={reload}
    />
  );
};

export default YearlyRollingCostKpi;
